import React, { useState, useEffect } from 'react';
import {
  Card, Button, Space, Typography, Row, Col, AutoComplete, Input, Drawer,
  List, Avatar, Tag, Badge, Checkbox, Table, Switch, Tooltip
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, WifiOutlined, ExclamationCircleOutlined,
  TeamOutlined, UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import logger from '../../services/logger';

const { Text } = Typography;

/**
 * DeviceSelector Component
 *
 * Tek cihaz modu: AutoComplete ile arama
 * Toplu mod: Checkbox'lı tablo, online/offline filtresi
 */
const DeviceSelector = ({
  mode = 'single', // 'single' | 'bulk'
  selectedDevice,
  selectedDevices = [],
  onSelectDevice,
  onSelectDevices,
  onModeChange
}) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [onlineOnly, setOnlineOnly] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/devices');
      setDevices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Devices fetch error:', error);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredDevices = devices.filter(device => {
    const matchesSearch =
      device.name.toLowerCase().includes(searchText.toLowerCase()) ||
      device.mac_address.toLowerCase().includes(searchText.toLowerCase());
    const matchesOnline = !onlineOnly || device.status === 'online';
    return matchesSearch && matchesOnline;
  });

  const onlineCount = devices.filter(d => d.status === 'online').length;
  const offlineCount = devices.filter(d => d.status !== 'online').length;
  const selectedOnlineCount = selectedDevices.filter(id =>
    devices.find(d => d.id === id && d.status === 'online')
  ).length;

  // Bulk mode table columns
  const bulkColumns = [
    {
      title: 'Cihaz',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <Badge status={record.status === 'online' ? 'success' : 'error'} />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'MAC',
      dataIndex: 'mac_address',
      key: 'mac_address',
      render: (mac) => <Text code style={{ fontSize: '11px' }}>{mac}</Text>,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'online' ? 'green' : 'red'} size="small">
          {status === 'online' ? 'Online' : 'Offline'}
        </Tag>
      ),
    },
    {
      title: 'Firmware',
      dataIndex: 'firmware_version',
      key: 'firmware_version',
      render: (v) => <Text type="secondary" style={{ fontSize: '11px' }}>{v || 'N/A'}</Text>,
    },
  ];

  const handleSelectAll = (checked) => {
    if (checked) {
      const ids = filteredDevices.map(d => d.id);
      onSelectDevices(ids);
    } else {
      onSelectDevices([]);
    }
  };

  const handleRowSelect = (deviceId, checked) => {
    if (checked) {
      onSelectDevices([...selectedDevices, deviceId]);
    } else {
      onSelectDevices(selectedDevices.filter(id => id !== deviceId));
    }
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      {/* Mode Toggle */}
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Text strong>Mod:</Text>
        </Col>
        <Col>
          <Space>
            <Button
              type={mode === 'single' ? 'primary' : 'default'}
              icon={<UserOutlined />}
              onClick={() => onModeChange('single')}
              size="small"
            >
              Tek Cihaz
            </Button>
            <Button
              type={mode === 'bulk' ? 'primary' : 'default'}
              icon={<TeamOutlined />}
              onClick={() => onModeChange('bulk')}
              size="small"
            >
              Toplu İşlem
            </Button>
          </Space>
        </Col>
        <Col flex="auto" />
        <Col>
          <Space>
            <Tag color="green">{onlineCount} Online</Tag>
            <Tag color="red">{offlineCount} Offline</Tag>
          </Space>
        </Col>
      </Row>

      {mode === 'single' ? (
        /* Single Device Mode */
        <Row gutter={16} align="middle">
          <Col>
            <Space size={4}>
              <Text style={{ fontSize: 12 }}>Online:</Text>
              <Switch checked={onlineOnly} onChange={setOnlineOnly} size="small" />
            </Space>
          </Col>
          <Col>
            <Space>
              <Text strong>Cihaz:</Text>
              {selectedDevice ? (
                <Space>
                  <Badge status={selectedDevice.status === 'online' ? 'success' : 'error'} />
                  <Tag color={selectedDevice.status === 'online' ? 'green' : 'default'}>
                    {selectedDevice.name}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    ({selectedDevice.mac_address})
                  </Text>
                </Space>
              ) : (
                <Text type="secondary">Seçilmedi</Text>
              )}
            </Space>
          </Col>
          <Col flex="auto">
            <AutoComplete
              style={{ width: '100%' }}
              placeholder="Cihaz ara... (isim veya MAC)"
              value={searchText}
              onChange={setSearchText}
              options={filteredDevices.map(device => ({
                value: `${device.name} (${device.mac_address})`,
                label: (
                  <Space>
                    <Badge status={device.status === 'online' ? 'success' : 'error'} />
                    <Text strong>{device.name}</Text>
                    <Text type="secondary">{device.mac_address}</Text>
                  </Space>
                ),
                device: device
              }))}
              onSelect={(value, option) => {
                onSelectDevice(option.device);
                setSearchText(value);
              }}
            />
          </Col>
          <Col>
            <Space>
              <Button icon={<SearchOutlined />} onClick={() => setDrawerVisible(true)} size="small">Tümü</Button>
              <Button icon={<ReloadOutlined />} onClick={fetchDevices} loading={loading} size="small" />
            </Space>
          </Col>
        </Row>
      ) : (
        /* Bulk Mode */
        <div>
          <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
            <Col span={8}>
              <Input
                placeholder="Cihaz ara..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col span={6}>
              <Space>
                <Text>Sadece Online:</Text>
                <Switch checked={onlineOnly} onChange={setOnlineOnly} size="small" />
              </Space>
            </Col>
            <Col span={6}>
              <Space>
                <Checkbox
                  checked={selectedDevices.length === filteredDevices.length && filteredDevices.length > 0}
                  indeterminate={selectedDevices.length > 0 && selectedDevices.length < filteredDevices.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  Tümünü Seç
                </Checkbox>
              </Space>
            </Col>
            <Col span={4}>
              <Button icon={<ReloadOutlined />} onClick={fetchDevices} loading={loading} size="small" />
            </Col>
          </Row>

          {/* Selection Summary */}
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f5f5f5', borderRadius: '4px' }}>
            <Space>
              <Text strong>Seçili: {selectedDevices.length} cihaz</Text>
              {selectedDevices.length > 0 && (
                <>
                  <Text type="secondary">
                    ({selectedOnlineCount} online, {selectedDevices.length - selectedOnlineCount} offline atlanacak)
                  </Text>
                  <Button type="link" size="small" onClick={() => onSelectDevices([])}>
                    Temizle
                  </Button>
                </>
              )}
            </Space>
          </div>

          {/* Device Table */}
          <Table
            dataSource={filteredDevices}
            columns={bulkColumns}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true }}
            rowSelection={{
              selectedRowKeys: selectedDevices,
              onChange: (keys) => onSelectDevices(keys),
              getCheckboxProps: (record) => ({
                disabled: onlineOnly && record.status !== 'online',
              }),
            }}
            scroll={{ y: 300 }}
          />
        </div>
      )}

      {/* Device List Drawer (for single mode) */}
      <Drawer
        title="Tüm Cihazlar"
        placement="right"
        width={500}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        extra={
          <Space>
            <Input
              placeholder="Ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 150 }}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchDevices} loading={loading} size="small" />
          </Space>
        }
      >
        <List
          dataSource={filteredDevices}
          loading={loading}
          renderItem={(device) => (
            <List.Item
              actions={[
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    onSelectDevice(device);
                    setSearchText(`${device.name} (${device.mac_address})`);
                    setDrawerVisible(false);
                  }}
                >
                  Seç
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    style={{ backgroundColor: device.status === 'online' ? '#52c41a' : '#ff4d4f' }}
                    icon={device.status === 'online' ? <WifiOutlined /> : <ExclamationCircleOutlined />}
                  />
                }
                title={
                  <Space>
                    <Text strong>{device.name}</Text>
                    <Tag color={device.status === 'online' ? 'green' : 'red'} size="small">
                      {device.status === 'online' ? 'Online' : 'Offline'}
                    </Tag>
                  </Space>
                }
                description={
                  <div>
                    <Text code style={{ fontSize: '11px' }}>{device.mac_address}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      Firmware: {device.firmware_version || 'N/A'}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>
    </Card>
  );
};

export default DeviceSelector;
